import { PurchaseType } from '@/enums/purchase-type';
import { PurchaseTypeLabels } from '@/enums/purchase-type-labels';

interface AmountLeg {
  type: string;
  amount: number;
}

/** "120,00 ₺" (tr-TR grouping/decimals, TL symbol as suffix). */
export const formatTenderAmount = (amount: number | string): string =>
  `${Number(amount).toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} ₺`;

/** Sum of leg amounts, rounded to kuruş so float noise never reaches the UI. */
export const sumTenderLegs = (legs: AmountLeg[]): number =>
  Math.round(legs.reduce((sum, leg) => sum + Number(leg.amount), 0) * 100) /
  100;

/** One line summary: "Kart 120,00 ₺, Nakit 80,00 ₺". */
export const summarizeTenderLegs = (
  legs: AmountLeg[],
  labelOf: (type: string) => string
): string =>
  legs
    .map((leg) => `${labelOf(leg.type)} ${formatTenderAmount(leg.amount)}`)
    .join(', ');

/**
 * POS device tender vocabulary mapped onto PurchaseType, so the existing
 * `purchase.type.*` i18n keys (Nakit, Fiziksel Kart, Yemek Kartı, Açık Hesap) are
 * reused. The backend records a device `card` leg as PHYSICAL_CARD.
 */
const POS_LEG_PURCHASE_TYPE = new Map<string, PurchaseType>([
  ['cash', PurchaseType.CASH],
  ['card', PurchaseType.PHYSICAL_CARD],
  ['meal', PurchaseType.MEAL_CARD],
  ['open_account', PurchaseType.OPEN_ACCOUNT]
]);

/** i18n key for a POS device tender type; unknown types fall back to raw type. */
export const posPaymentLegLabelKey = (type: string): string => {
  const purchaseType = POS_LEG_PURCHASE_TYPE.get(type);
  return purchaseType ? PurchaseTypeLabels[purchaseType] : type;
};
